"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function WeddingShowcaseServices({ section }) {
  return (
    <section className="py-16 relative overflow-hidden bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">

          {/* Left Column: Premium Framed Image with Soft Rose Glow */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative lg:col-span-7 w-full"
          >
            {/* Soft decorative rose/pink glow behind the image */}
            <div className="absolute inset-0 bg-[#f43f5e]/15 rounded-[32px] blur-3xl -z-10 transform scale-95" />

            <div className="p-1.5 bg-white/5 border border-white/10 rounded-[24px] shadow-2xl backdrop-blur-md hover:scale-[1.01] transition-transform duration-300">
              {section.image && section.image.trim() !== "" && (
                <Image
                  src={section.image}
                  alt="Wedding Website Builder"
                  className="w-full h-auto rounded-[18px] object-cover border border-white/10 shadow-inner"
                  width={700}
                  height={450}
                />
              )}
            </div>
          </motion.div>

          {/* Right Column: Copywriting & Value Props */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col gap-5 lg:col-span-5"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Showcase your{" "}
              <span className="relative inline-block text-[#f43f5e]">
                <span className="relative z-10">weddings</span>
                {/* Elegant Heart loop underline */}
                <svg
                  className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90 text-[#f43f5e]"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h2>

            <div className="space-y-5">
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
              <p className="leading-relaxed text-gray-300 text-xl" dangerouslySetInnerHTML={{ __html: section.text2 }} />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function WeddingQuotations({ section }) {
  return (
    <section className="py-16 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">

        {/* Title Block */}
        <div className="mb-12">
          <div className="pb-3 mb-2">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}
            >
              Send{" "}
              <span className="relative inline-block text-amber-400 font-bold mx-2">
                <span className="relative z-10">stunning</span>
                {/* Yellow Wavy Underline */}
                <svg
                  className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90 text-[#fbbf24]"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              quotations
            </motion.h2>
          </div>

          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <p className="leading-relaxed text-gray-300 mb-0 font-medium text-xl" dangerouslySetInnerHTML={{ __html: section.text1 }} />
            </motion.div>
          </div>
        </div>

        {/* 4-Image Mosaic Grid Section */}
        <div className="relative w-full max-w-[1250px] mx-auto py-4 flex justify-center">
          <div className="absolute inset-0 bg-[#fbbf24]/5 rounded-full blur-3xl -z-10 transform scale-75" />

          <div className="grid grid-cols-2 gap-2 md:gap-6 items-center w-full">
            {/* Left Pair */}
            <div className="flex items-center justify-center md:justify-end py-2 md:py-0">
              {section.images?.[0] && section.images[0].trim() !== "" && (
                <motion.div
                  initial={{ opacity: 0, y: 20, rotate: -2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="w-[72%] p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl z-10 translate-y-4 -mr-[25%] hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[0]}
                    alt="Quotation Details"
                    className="w-full h-auto rounded-xl object-cover"
                    width={300}
                    height={400}
                  />
                </motion.div>
              )}

              {section.images?.[1] && section.images[1].trim() !== "" && (
                <motion.div
                  initial={{ opacity: 0, y: -20, rotate: 2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="w-[72%] p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl z-0 -translate-y-4 hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[1]}
                    alt="Quotation Editor"
                    className="w-full h-auto rounded-xl object-cover"
                    width={300}
                    height={400}
                  />
                </motion.div>
              )}
            </div>

            {/* Right Pair */}
            <div className="flex items-center justify-center md:justify-start py-2 md:py-0 relative">

              {/* Surprised Emoji peeking from behind the fourth card */}
              {section.emojiIcon && section.emojiIcon.trim() !== "" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0, rotate: 0 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: 20 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 100, delay: 0.4 }}
                  className="absolute -top-12 right-2 w-16 md:w-20 h-auto z-30 pointer-events-none"
                >
                  <Image
                    src={section.emojiIcon}
                    alt=""
                    width={80}
                    height={80}
                    className="w-full h-auto"
                  />
                </motion.div>
              )}

              {section.images?.[2] && section.images[2].trim() !== "" && (
                <motion.div
                  initial={{ opacity: 0, y: -20, rotate: -2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="w-[72%] p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl z-0 -translate-y-4 hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[2]}
                    alt="Mobile Quotation Preview"
                    className="w-full h-auto rounded-xl object-cover"
                    width={300}
                    height={400}
                  />
                </motion.div>
              )}

              {section.images?.[3] && section.images[3].trim() !== "" && (
                <motion.div
                  initial={{ opacity: 0, y: 20, rotate: 2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="w-[72%] p-1.5 bg-white border border-gray-200 rounded-2xl shadow-2xl z-20 translate-y-4 -ml-[25%] hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[3]}
                    alt="Quotation Total Invoice"
                    className="w-full h-auto rounded-xl object-cover"
                    width={300}
                    height={400}
                  />
                </motion.div>
              )}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export function WeddingCoordination({ section }) {
  return (
    <section className="py-16 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">

        {/* Title */}
        <div className="text-center max-w-4xl mx-auto mb-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white text-center leading-tight mb-4`}
          >
            Turn{" "}
            <span className="relative inline-block text-rose-400 font-bold mx-2">
              <span className="relative z-10">sales orders</span>
              {/* Red Scribble Underline */}
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90 text-[#f43f5e]"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            into{" "}
            <span className="relative inline-block text-rose-400 font-bold mx-2">
              <span className="relative z-10">projects</span>
              {/* Red Scribble Underline */}
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90 text-[#f43f5e]"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </motion.h2>
        </div>

        {/* Three Columns Copy */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-left"
          >
            <p className="leading-relaxed text-xl text-gray-300" dangerouslySetInnerHTML={{ __html: section.text1 }} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-left"
          >
            <p className="leading-relaxed text-xl text-gray-300" dangerouslySetInnerHTML={{ __html: section.text2 }} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-left"
          >
            <p className="leading-relaxed text-xl text-gray-300" dangerouslySetInnerHTML={{ __html: section.text3 }} />
          </motion.div>
        </div>

        {/* Dynamic Image / Flow section */}
        <div className="flex flex-row items-center justify-center gap-6 sm:gap-10 relative max-w-[950px] mx-auto py-6">
          {/* Background Glow */}
          <div className="absolute inset-0 bg-[#f43f5e]/5 rounded-full blur-3xl -z-10 transform scale-75" />

          {/* Left card: Invoice list */}
          {section.images?.[0] && section.images[0].trim() !== "" && (
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-[48%] max-w-[380px] p-1 bg-white border border-gray-200 rounded-2xl shadow-xl hover:scale-[1.01] transition-transform duration-300 relative z-10"
            >
              <Image
                src={section.images[0]}
                alt="Odoo Quotation Invoice"
                className="w-full h-auto rounded-xl object-cover"
                width={380}
                height={500}
              />
            </motion.div>
          )}

          {/* Middle: Curved Arrow pointing right */}
          {section.arrowIcon && section.arrowIcon.trim() !== "" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 0.8, scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 100, delay: 0.3 }}
              className="flex-shrink-0 z-20 pointer-events-none select-none w-14 h-auto"
            >
              <Image
                src={section.arrowIcon}
                alt=""
                className="w-full h-auto rotate-[80deg] scale-x-[-1]"
                style={{ filter: "invert(0.3) sepia(1) saturate(1.5) hue-rotate(320deg) brightness(0.85)" }}
                width={56}
                height={56}
              />
            </motion.div>
          )}

          {/* Right card: Project details card */}
          {section.images?.[1] && section.images[1].trim() !== "" && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="w-[35%] max-w-[280px] p-1 bg-white border border-gray-200 rounded-2xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative z-10"
            >
              <Image
                src={section.images[1]}
                alt="Odoo Project Card"
                className="w-full h-auto rounded-xl object-cover"
                width={280}
                height={360}
              />
            </motion.div>
          )}

        </div>

      </div>
    </section>
  );
}

export function WeddingLogistics({ section }) {
  return (
    <section className="py-16 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">

        {/* Title Header */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4`}
          >
            Manage{" "}
            <span className="relative inline-block text-rose-400">
              <span className="relative z-10">logistics</span>
              {/* Rose Underline */}
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90 text-[#f43f5e]"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            flawlessly
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="leading-relaxed text-gray-300 text-xl"
            dangerouslySetInnerHTML={{ __html: section.text }}
          />
        </div>

        {/* Three Columns Grid */}
        <div className="grid md:grid-cols-3 gap-8 relative">
          <div className="absolute inset-0 bg-[#f43f5e]/5 rounded-full blur-3xl -z-10 transform scale-50" />

          {section.items.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="flex flex-col gap-3 group cursor-pointer"
            >
              <h4 className={`${caveat.className} text-3xl md:text-4xl font-bold text-white mb-2 text-left group-hover:text-rose-400 transition-colors leading-tight`}>
                {item.label}
              </h4>

              {item.image && item.image.trim() !== "" && (
                <div className="w-full bg-white border border-gray-200 rounded-2xl p-1.5 shadow-xl hover:scale-[1.02] hover:shadow-2xl transition-all duration-300">
                  <Image
                    src={item.image}
                    alt={item.label}
                    className="w-full h-auto rounded-xl object-cover"
                    width={400}
                    height={280}
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}

export function WeddingCrm({ section }) {
  return (
    <section className="py-16 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">

        {/* Title Block */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}
          >
            Manage your{" "}
            <span className="relative inline-block text-sky-400 font-bold mx-2">
              <span className="relative z-10">leads</span>
              {/* Blue hand-drawn wavy underline */}
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90 text-[#38bdf8]"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            and{" "}
            <span className="relative inline-block text-sky-400 font-bold mx-2">
              <span className="relative z-10">business prospects</span>
              {/* Blue hand-drawn wavy underline */}
              <svg
                className="absolute -bottom-2 left-0 w-full h-4 overflow-visible opacity-90 text-[#38bdf8]"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 8 C 25 4, 60 3, 95 6 C 70 8, 30 9, 5 8 C 28 6, 62 5, 92 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </motion.h2>
        </div>

        {/* Two Columns Grid */}
        <div className="grid lg:grid-cols-12 gap-12 items-center">

          {/* Left Column: Copywriting */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-6 lg:col-span-5 order-2 lg:order-1 pt-6 lg:pt-0"
          >
            <div className="space-y-6">
              <p className="leading-relaxed text-gray-300 text-xl">
                <strong className="text-white">Follow each step of your sales process,</strong> from first contact to closing the deal.
              </p>
              <p className="leading-relaxed text-gray-300 text-xl">
                Set reminders, add events to your calendar, and keep all your leads active.
              </p>
              <p className="leading-relaxed text-gray-300 text-xl">
                Handle contracts, signatures, and formal agreements directly in the CRM.
              </p>
            </div>
          </motion.div>

          {/* Right Column: CRM Pipeline Image with Rays Graphic */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative lg:col-span-7 w-full order-1 lg:order-2"
          >
            {/* Blue Decorative Glow behind Image */}
            <div className="absolute inset-0 bg-[#38bdf8]/10 rounded-[32px] blur-3xl -z-10 transform scale-95" />

            {/* Blue Hand-drawn Spark Rays (Pointer sparks) in top right */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 0.9 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 100, delay: 0.4 }}
              className="absolute -top-10 -right-8 w-24 h-24 pointer-events-none select-none z-20 text-sky-400"
            >
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full stroke-current" strokeWidth="4" strokeLinecap="round">
                <line x1="50" y1="50" x2="30" y2="25" />
                <line x1="55" y1="45" x2="45" y2="15" />
                <line x1="60" y1="50" x2="80" y2="25" />
              </svg>
            </motion.div>

            <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300">
              {section.image && section.image.trim() !== "" && (
                <Image
                  src={section.image}
                  alt="Wedding Planner CRM Pipeline"
                  className="w-full h-auto rounded-[18px] object-cover"
                  width={700}
                  height={450}
                />
              )}
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

export function WeddingSign({ section }) {
  return (
    <section className="py-16 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px]">

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Left Column: Adopt Signature dialog block & thumbs-up icon */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative lg:col-span-6 w-full order-1 lg:order-1 flex justify-center"
          >
            {/* Round Backdrop Shape */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 aspect-square w-[80%] max-w-[450px] bg-white/[0.03] rounded-full -z-10" />
            <div className="absolute inset-0 bg-[#10b981]/5 rounded-[32px] blur-3xl -z-20 transform scale-95" />

            {/* The Signature Mockup Box */}
            <div className="relative w-full max-w-[380px] p-1 bg-white border border-gray-200 rounded-[28px] shadow-2xl hover:scale-[1.01] transition-transform duration-300">
              {section.image && section.image.trim() !== "" && (
                <Image
                  src={section.image}
                  alt="Odoo Sign online contract dialog"
                  className="w-full h-auto rounded-[22px] object-cover"
                  width={380}
                  height={500}
                />
              )}

              {/* Peeking thumbs-up emoji on the right edge */}
              {section.emojiIcon && section.emojiIcon.trim() !== "" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0, rotate: -20 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: -10 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 120, delay: 0.4 }}
                  className="absolute top-[48%] -right-10 w-16 md:w-20 h-auto z-20 pointer-events-none drop-shadow-2xl"
                >
                  <Image
                    src={section.emojiIcon}
                    alt=""
                    width={80}
                    height={80}
                    className="w-full h-auto"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Right Column: Copywriting & Value Props */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex flex-col gap-6 lg:col-span-6 order-2 lg:order-2"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Sign and close a{" "}
              <span className="relative inline-block text-black font-bold mx-2">
                {/* Thick Green highlighter marker background */}
                <span className="absolute inset-0 bg-emerald-400 rounded-lg transform -rotate-1 skew-x-6 z-0 shadow-md scale-y-105" />
                <span className="relative z-10 px-3 py-0.5">deal</span>
              </span>
            </h2>

            <div className="space-y-4">
              <p className="leading-relaxed text-gray-300 font-semibold text-xl">
                Simplify and automate signing contracts online.
              </p>
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}
